import { FIELD_NAME_KEY } from "PFApp/constants/activity_form/activity_form_field_keys";
import css from "PFApp/hooks/activity_form/multi_roles_form/use_actions_column/actions_cell.module.scss";
import { useManageRolesStore } from "PFApp/stores/manage_roles";
import { RoleFormKey } from "PFApp/types/activity_form/activity_form_schema";
import { Stack } from "PFComponents/containers";
import { DropdownButton } from "PFComponents/dropdown_button";
import { Pill, PillKind } from "PFComponents/pill";
import { Tooltip } from "PFComponents/tooltip";
import { ActivityState, Id } from "PFCore/types";
import { IconName } from "PFTheme/graphics/icons";
import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ManageRolesActionType } from "../manage_roles.types";

type StatusPillAttributes = {
  kind: PillKind;
  icon: IconName;
  tooltipText: string;
  ariaLabel: string;
};

type ManageRolesActionsCellProps = {
  roleFormKey: RoleFormKey;
  onChange: (roleFormKey: RoleFormKey, action: ManageRolesActionType) => void;
};

export const ManageRolesActionsCell = ({ roleFormKey, onChange }: ManageRolesActionsCellProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "engagementBlueprint" });

  const roleId = useWatch({ name: `${roleFormKey}.id` }) as Id | undefined;
  const roleName = useWatch({ name: `${roleFormKey}.${FIELD_NAME_KEY}` });
  const state = useWatch({ name: `${roleFormKey}.state` }) as ActivityState | undefined;
  const isFillAndBookable = useWatch({ name: `${roleFormKey}.is_fill_and_bookable` }) as boolean | undefined;

  const hasFailed = useManageRolesStore((state) => {
    if (!roleId) {
      return true;
    }
    return Object.values(state.unsavedEdits).some((edit) => edit.roleId === roleId && edit.isError);
  });

  const statusPill = useMemo((): StatusPillAttributes => {
    if (hasFailed) {
      const tooltipText = t("status.failedToSave");
      return {
        kind: "warning",
        icon: "warning",
        tooltipText,
        ariaLabel: `${roleName}: ${tooltipText}`
      };
    }
    const tooltipText = t("status.saved");
    return {
      kind: "success",
      icon: "check",
      tooltipText,
      ariaLabel: `${roleName}: ${tooltipText}`
    };
  }, [hasFailed, roleName, t]);

  const options = useMemo(
    () => [
      {
        id: ManageRolesActionType.EditAvailability,
        displayElement: t("actions.editAvailability"),
        item: ManageRolesActionType.EditAvailability
      },
      ...(state === "draft"
        ? [
            {
              id: ManageRolesActionType.Publish,
              displayElement: t("actions.publish"),
              item: ManageRolesActionType.Publish,
              disabled: !roleId
            }
          ]
        : []),
      ...(isFillAndBookable
        ? [
            {
              id: ManageRolesActionType.FillAndBook,
              displayElement: t("actions.fillAndBook"),
              item: ManageRolesActionType.FillAndBook
            }
          ]
        : [])
    ],
    [isFillAndBookable, roleId, state, t]
  );

  return (
    <Stack alignItems="center" justifyContent="start" className={css.actionCellContent} gap="spacingMd">
      <DropdownButton
        actionIcon="menu-vertical"
        small
        options={options}
        handleChange={(item) => onChange(roleFormKey, item)}
        dropDownStyle={{ overflow: "visible" }}
        aria-label={t("actions.moreOptionsForRole", { roleName: roleName || t("emptyRoleName") })}
        floatingOptions={{ strategy: "fixed" }}
      />
      <Tooltip content={statusPill.tooltipText}>
        <Pill kind={statusPill.kind} icon={statusPill.icon} small aria-label={statusPill.ariaLabel} />
      </Tooltip>
    </Stack>
  );
};
